import { runner } from '@studio/js-utils/async';
import { normalizeId } from '@studio/models';
import { setPendingDevice } from '@studio/models/device';
import type { Agent } from '@studio/models/agent';
import { broker, restoreAgent } from '#~/agent/broker.js';
import { seedAgentSnapshotForTests } from '#~/agent/snapshots.js';
import type { Proxy, Snapshot } from '#~/agent/types.js';
import type { EventCategory } from '#~/analytics/constants/event-categories.js';
import type { EventName } from '#~/analytics/constants/event-names.js';
import {
  armDeviceShadowTimeout,
  disarmDeviceShadowTimeout,
  isDeviceShadowTimeoutArmed
} from '#~/analytics/domains/shadow-timeout.js';
import { track } from '#~/analytics/track.js';
import type { EventProperties } from '#~/analytics/types/properties.js';
import { toIdFromHex } from '#~/api/index.js';
import { toEmbeddedShadowDeviceId } from '#~/device/embedded-shadow-id.js';
import { depot } from '#~/depot/index.js';
import { ElectronAPI } from '#~/electron/api.js';
import { reportClaimLinked, resolveClaimLinkResponse } from '#~/routes/agent/link/link-response.js';
import {
  reportAddSourceCredentialReply,
  type AddSourceCredentialReply
} from '#~/video/components/add/source/dialogs/credential-reply.js';
import {
  enableClaimBootstrapObservation,
  readClaimBootstrapAgentStatus,
  readClaimBootstrapObservation
} from './claim-bootstrap-observation.js';
import { readDeviceDiagnostics } from './device-diagnostics.js';

type DeviceGrainProbeInput =
  | { event: 'auth-invalid'; agentId: string; studioId?: string }
  | { event: 'auth-locked'; agentId: string; studioId?: string }
  | { event: 'auth-not-401'; agentId: string; studioId?: string }
  | { event: 'claim-linked'; agentId: string; studioId: string; embedded: boolean }
  | { event: 'add-source-reply'; reply: AddSourceCredentialReply; studioId: string }
  | { event: 'shadow-timeout'; agentId: string; studioId?: string }
  | {
      event: 'shadow-after-claim';
      agentId: string;
      studioId: string;
      modelId: string;
      claimTimeoutMs: number;
    }
  | { event: 'shadow-add-fails'; agentId: string; studioId: string; modelId: string }
  | {
      event: 'shadow-add-not-inserted';
      agentId: string;
      studioId: string;
      modelId: string;
      placeholderHoldMs: number;
    }
  | {
      event: 'claim-then-shadow-elsewhere';
      agentId: string;
      claimStudioId: string;
      shadowStudioId: string;
      modelId: string;
    };

type DeviceGrainProbeResult = {
  /** The generated shadow camera id, for the bootstrap events. */
  deviceId?: string;
  /** Whether the claim clock was still running once the shadow emitted. */
  claimClockArmedAfterShadow?: boolean;
  /** Whether `claim-linked` left the claim clock running. */
  claimClockArmed?: boolean;
  /** The Add Source reply's classification, for `add-source-reply`. */
  outcome?: string;
  /** Whether the claim Studio's clock was still running after another Studio shadowed. */
  claimStudioClockArmedAfterShadow?: boolean;
};

const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

if (isE2eEnabled()) {
  enableClaimBootstrapObservation();
  // The exact claimed proxy may be absent even after authentication succeeds.
  // Retain passive phases rather than guessing which unrelated request failed.
  Reflect.set(globalThis, '__irisReadClaimBootstrap', (agentId: string, roomId: string) =>
    readClaimBootstrapObservation(
      [...broker]
        .filter(
          (proxy) =>
            proxy.id.toLowerCase() === agentId.toLowerCase() &&
            proxy.roomId.toLowerCase() === roomId.toLowerCase()
        )
        .map((proxy) => ({
          proxy,
          started: proxy.isStarted,
          stateHasGeneratedCamera:
            proxy.state.control?.device?.[toEmbeddedShadowDeviceId(proxy.id)] !== undefined
        })),
      agentId,
      roomId,
      readClaimBootstrapAgentStatus(
        Object.values(depot.getState().agents).find(
          (agent) => agent.id.toLowerCase() === agentId.toLowerCase()
        ),
        roomId
      )
    )
  );
  // Pause only an actual decoded camera attachment. The normal Video watchdog
  // must notice and repair playback; this receipt never drives its controller
  // or fabricates a progress event. Dynamic imports keep this test-only path
  // out of ordinary renderer startup.
  Reflect.set(
    globalThis,
    '__irisPauseDeviceVideo',
    async (
      inputDeviceId: string
    ): Promise<{
      pausedElements: number;
      deviceId: string;
      agentId: string;
      isEmbedded: boolean;
      mediaStreamTrackId: string;
    }> => {
      const [{ toDeviceId }, { locateProxy }, { locateLiveKit }] = await Promise.all([
        import('@studio/models/device'),
        import('#~/agent/utils.js'),
        import('#~/livekit/utils.js')
      ]);
      const deviceId = toDeviceId(inputDeviceId);
      const proxy = locateProxy(deviceId);
      const liveKit = locateLiveKit(deviceId);
      if (proxy === undefined || proxy === null || liveKit === undefined || liveKit === null) {
        throw new Error('Camera recovery receipt requires a managing agent and LiveKit room');
      }
      const track = liveKit.videoForAgent(proxy.id, proxy.embedded).get(deviceId);
      if (track === undefined) throw new Error('Camera recovery receipt requires a video track');
      const videos = track.attachedElements.filter(
        (element): element is HTMLVideoElement =>
          element instanceof HTMLVideoElement &&
          element.isConnected &&
          !element.paused &&
          element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
          element.videoWidth > 0 &&
          element.videoHeight > 0 &&
          element.srcObject instanceof MediaStream &&
          element.srcObject.getVideoTracks().some((media) => media.id === track.mediaStreamTrack.id)
      );
      if (videos.length === 0) {
        throw new Error('Camera recovery receipt requires an attached decoded playing video');
      }
      for (const video of videos) video.pause();
      return {
        pausedElements: videos.length,
        deviceId,
        agentId: proxy.id,
        isEmbedded: proxy.embedded,
        mediaStreamTrackId: track.mediaStreamTrack.id
      };
    }
  );

  type DeferredShadowMode =
    | 'slow-save-resolve'
    | 'slow-save-reject'
    | 'late-insertion-resolve'
    | 'late-insertion-reject';
  const shadowCompletions = new Map<string, () => Promise<void>>();
  const shadowStates = new Map<
    string,
    () => { elapsedSincePrepared: number; held: boolean; inserted: boolean }
  >();
  Reflect.set(globalThis, '__irisShadowAddTelemetryState', (agentId: string) => {
    const read = shadowStates.get(agentId);
    if (read === undefined) throw new Error('Deferred shadow receipt does not own this id');
    return read();
  });

  // This read-only receipt proves that the tray has mounted its room broker,
  // rather than merely having loaded a page with a preload script.
  Reflect.set(globalThis, '__irisDeviceGrainBrokerState', (deviceId?: string) =>
    [...broker].map((proxy) => ({
      agentId: proxy.id,
      roomId: proxy.roomId,
      started: proxy.isStarted,
      // A failed media receipt needs every owner's matching status, including
      // duplicate control entries that could overwrite the query's result.
      ...(typeof deviceId === 'string'
        ? {
            controlsDevice: proxy.state.control?.device?.[deviceId] !== undefined,
            device: readDeviceDiagnostics(proxy, deviceId)
          }
        : {})
    }))
  );

  // Hold one external mutation/save response while preserving the real broker,
  // single-flight lock, bootstrap timers and analytics path. The proxy wrapper
  // isolates writes to a synthetic camera; it never writes a real bridge.
  Reflect.set(
    globalThis,
    '__irisPrepareShadowAddTelemetry',
    async (input: {
      agentId: string;
      studioId: string;
      modelId: string;
      mode: DeferredShadowMode;
    }): Promise<{ deviceId: string }> => {
      const agentId = normalizeId<Agent['id']>(input.agentId);
      const target = [...broker][0];
      if (agentId === undefined || target === undefined) {
        throw new Error('Deferred shadow receipt requires a hex id and a mounted room broker');
      }
      if (shadowCompletions.size > 0)
        throw new Error('Deferred shadow receipt already owns this renderer');
      const deviceId = toEmbeddedShadowDeviceId(agentId);
      const devices: Record<string, unknown> = {};
      const state = {
        control: { device: devices, studio: {} },
        status: { device: { [deviceId]: {} } }
      };
      let release: () => void = () => {};
      let entered: () => void = () => {};
      let enteredAt = 0;
      let responseHeld = true;
      const held = new Promise<void>((resolve) => {
        release = resolve;
      });
      const ready = new Promise<void>((resolve) => {
        entered = resolve;
      });
      const awaitResponse = async (): Promise<void> => {
        enteredAt = Date.now();
        entered();
        await held;
        responseHeld = false;
        if (input.mode.endsWith('reject')) throw new Error('Receipt-owned operation rejected');
      };
      const synthetic = new globalThis.Proxy(target, {
        get(current, key) {
          if (key === 'id') return agentId;
          if (key === 'roomId') return input.studioId;
          if (key === 'embedded') return true;
          if (key === 'modelId') return toIdFromHex(input.modelId);
          if (key === 'name') return 'e2e deferred embedded camera';
          if (key === 'state') return state;
          if (key === 'emit') return async () => {};
          if (key === 'mutate')
            return async (...args: Parameters<Proxy['mutate']>) => {
              if (input.mode.startsWith('late-insertion')) await awaitResponse();
              Object.assign(state.control.device, args[0].control?.device);
            };
          if (key === 'save')
            return async () => {
              if (input.mode.startsWith('slow-save')) await awaitResponse();
            };
          const value = Reflect.get(current, key, current);
          return typeof value === 'function' ? value.bind(current) : value;
        }
      });
      const token = await ElectronAPI.getAccessToken();
      const acquire = broker.acquire;
      broker.acquire = function (...args) {
        return args[0] === agentId ? synthetic : acquire.apply(this, args);
      };
      // Restore fails closed on an unreadable snapshot (#1858); this camera has none stored.
      seedAgentSnapshotForTests(synthetic, {} as Snapshot);
      const operation = runner(() => restoreAgent(synthetic, token, ElectronAPI.getAccessToken));
      shadowCompletions.set(agentId, async () => {
        release();
        try {
          const result = await operation;
          if (result.completed !== true)
            throw new Error('Deferred shadow bootstrap did not complete');
        } finally {
          broker.acquire = acquire;
          shadowCompletions.delete(agentId);
          shadowStates.delete(agentId);
        }
      });
      shadowStates.set(agentId, () => ({
        elapsedSincePrepared: Date.now() - enteredAt,
        held: responseHeld,
        inserted: devices[deviceId] !== undefined
      }));
      // Reject readiness if the bootstrap exits before reaching the controlled
      // external response, instead of letting a setup error masquerade as RED.
      try {
        await Promise.race([
          ready,
          operation.then(() => {
            throw new Error('Deferred shadow never reached its owned response');
          })
        ]);
      } catch (cause) {
        release();
        await operation;
        broker.acquire = acquire;
        shadowCompletions.delete(agentId);
        shadowStates.delete(agentId);
        throw cause;
      }
      return { deviceId };
    }
  );
  Reflect.set(
    globalThis,
    '__irisFinishShadowAddTelemetry',
    async (agentId: string): Promise<void> => {
      const finish = shadowCompletions.get(agentId);
      if (finish === undefined) throw new Error('Deferred shadow receipt does not own this id');
      await finish();
    }
  );

  const emit = (name: EventName, category: EventCategory, properties: EventProperties): void => {
    void track({
      name,
      category,
      properties,
      timestamp: Date.now()
    });
  };

  /**
   * Bootstrap an embedded agent through the real restoreAgent. The proxy is a
   * stand-in for a linked, authority-ready camera's agent. Its shadow camera is already in state, so
   * the broker's add retries treat it as a reconnect and leave no add outcome
   * (and no extra camera in any real Studio) behind.
   */
  const shadowAfterClaim = async (
    input: Extract<DeviceGrainProbeInput, { event: 'shadow-after-claim' }>
  ): Promise<DeviceGrainProbeResult> => {
    const agentId = normalizeId<Agent['id']>(input.agentId);
    if (agentId === undefined) {
      throw new Error('[device-grain-probe] shadow-after-claim needs a hex agent id');
    }
    const deviceId = toEmbeddedShadowDeviceId(agentId);
    armDeviceShadowTimeout({
      agentId,
      studioId: input.studioId,
      track: emit,
      timeoutMs: input.claimTimeoutMs
    });
    const agent = {
      id: agentId,
      roomId: input.studioId,
      embedded: true,
      // This synthetic camera already has an accepted authority snapshot.
      isStarted: true,
      modelId: toIdFromHex(input.modelId),
      name: 'e2e embedded camera',
      // The camera is already in state and reporting status, so the add
      // retries treat it as a reconnect and stop after ten ticks instead of
      // running a hundred in the shared session.
      state: {
        control: { device: { [deviceId]: {} }, studio: {} },
        status: { device: { [deviceId]: {} } }
      },
      emit: async () => {},
      // restoreAgent subscribes to the connection's lifetime (#1858).
      on: () => () => {}
    } as unknown as Proxy;
    seedAgentSnapshotForTests(agent, {} as Snapshot);
    const token = await ElectronAPI.getAccessToken();
    const result = await runner(() => restoreAgent(agent, token, ElectronAPI.getAccessToken));
    if (result.completed !== true) {
      throw new Error('[device-grain-probe] restoreAgent did not complete');
    }
    return {
      deviceId,
      claimClockArmedAfterShadow: isDeviceShadowTimeoutArmed(agentId, input.studioId)
    };
  };

  /**
   * Bootstrap an embedded agent whose shadow camera can never be added. The
   * broker holds no proxy for this agent, so every add attempt throws before
   * any write, and the camera reports status so the retries stop after ten
   * ticks. The real restoreAgent then settles the episode as device_add_failed.
   */
  const shadowAddFails = async (
    input: Extract<DeviceGrainProbeInput, { event: 'shadow-add-fails' }>
  ): Promise<DeviceGrainProbeResult> => {
    const agentId = normalizeId<Agent['id']>(input.agentId);
    if (agentId === undefined) {
      throw new Error('[device-grain-probe] shadow-add-fails needs a hex agent id');
    }
    const deviceId = toEmbeddedShadowDeviceId(agentId);
    const agent = {
      id: agentId,
      roomId: input.studioId,
      embedded: true,
      // Add failure follows startup; it must not model an unanswered authority request.
      isStarted: true,
      modelId: toIdFromHex(input.modelId),
      name: 'e2e embedded camera',
      state: { control: { device: {}, studio: {} }, status: { device: { [deviceId]: {} } } },
      emit: async () => {},
      // restoreAgent subscribes to the connection's lifetime (#1858).
      on: () => () => {}
    } as unknown as Proxy;
    seedAgentSnapshotForTests(agent, {} as Snapshot);
    const token = await ElectronAPI.getAccessToken();
    const result = await runner(() => restoreAgent(agent, token, ElectronAPI.getAccessToken));
    if (result.completed !== true) {
      throw new Error('[device-grain-probe] restoreAgent did not complete');
    }
    return { deviceId };
  };

  /**
   * Bootstrap an embedded agent whose every add attempt stands back without
   * throwing, so the episode ends with the camera never inserted
   * (`add_not_inserted`). The stand-in is the pending placeholder the room
   * provider shows while a camera loads: addDevice returns false while it is
   * fresh, and settling waits for it to expire. The probe refreshes it until
   * the broker's retry loop has stopped (ten 2.5s status ticks), then lets its
   * 10s TTL run out, after which the real restoreAgent settles the episode.
   */
  const shadowAddNotInserted = async (
    input: Extract<DeviceGrainProbeInput, { event: 'shadow-add-not-inserted' }>
  ): Promise<DeviceGrainProbeResult> => {
    const agentId = normalizeId<Agent['id']>(input.agentId);
    if (agentId === undefined) {
      throw new Error('[device-grain-probe] shadow-add-not-inserted needs a hex agent id');
    }
    const deviceId = toEmbeddedShadowDeviceId(agentId);
    const holdPlaceholder = (): void => {
      setPendingDevice({ id: deviceId, name: 'e2e embedded camera', embedded: true, agentId });
    };
    holdPlaceholder();
    const refresher = setInterval(holdPlaceholder, 2_000);
    setTimeout(() => clearInterval(refresher), input.placeholderHoldMs);
    const agent = {
      id: agentId,
      roomId: input.studioId,
      embedded: true,
      // The pending camera insertion belongs to an already-started agent.
      isStarted: true,
      modelId: toIdFromHex(input.modelId),
      name: 'e2e embedded camera',
      state: { control: { device: {}, studio: {} }, status: { device: { [deviceId]: {} } } },
      emit: async () => {},
      // restoreAgent subscribes to the connection's lifetime (#1858).
      on: () => () => {}
    } as unknown as Proxy;
    seedAgentSnapshotForTests(agent, {} as Snapshot);
    const token = await ElectronAPI.getAccessToken();
    const result = await runner(() => restoreAgent(agent, token, ElectronAPI.getAccessToken));
    if (result.completed !== true) {
      clearInterval(refresher);
      throw new Error('[device-grain-probe] restoreAgent did not complete');
    }
    return { deviceId };
  };

  /**
   * A claim settles in one Studio (its clock starts) and the same camera then
   * shadows into another Studio through the real restoreAgent. The second
   * Studio's shadow must not stop the first Studio's clock: the camera has not
   * reached the Studio the user claimed it into. The camera is already in the
   * shadow Studio's state, so the add retries treat it as a reconnect.
   */
  const claimThenShadowElsewhere = async (
    input: Extract<DeviceGrainProbeInput, { event: 'claim-then-shadow-elsewhere' }>
  ): Promise<DeviceGrainProbeResult> => {
    const agentId = normalizeId<Agent['id']>(input.agentId);
    if (agentId === undefined) {
      throw new Error('[device-grain-probe] claim-then-shadow-elsewhere needs a hex agent id');
    }
    reportClaimLinked({
      agentId,
      studioId: input.claimStudioId,
      embedded: true,
      track: emit
    });
    const deviceId = toEmbeddedShadowDeviceId(agentId);
    const agent = {
      id: agentId,
      roomId: input.shadowStudioId,
      embedded: true,
      // This Studio already received the synthetic camera's authority snapshot.
      isStarted: true,
      modelId: toIdFromHex(input.modelId),
      name: 'e2e embedded camera',
      state: {
        control: { device: { [deviceId]: {} }, studio: {} },
        status: { device: { [deviceId]: {} } }
      },
      emit: async () => {},
      // restoreAgent subscribes to the connection's lifetime (#1858).
      on: () => () => {}
    } as unknown as Proxy;
    seedAgentSnapshotForTests(agent, {} as Snapshot);
    try {
      const token = await ElectronAPI.getAccessToken();
      const result = await runner(() => restoreAgent(agent, token, ElectronAPI.getAccessToken));
      if (result.completed !== true) {
        throw new Error('[device-grain-probe] restoreAgent did not complete');
      }
      return {
        deviceId,
        claimStudioClockArmedAfterShadow: isDeviceShadowTimeoutArmed(agentId, input.claimStudioId)
      };
    } finally {
      // No camera follows this synthetic claim, so stop its clock rather than
      // leave a 60s timeout to fire into later specs in the same session.
      disarmDeviceShadowTimeout(agentId, input.claimStudioId);
    }
  };

  const simulate = async (input: DeviceGrainProbeInput): Promise<DeviceGrainProbeResult> => {
    if (input.event === 'auth-invalid') {
      // The claim screen's own reply classifier, fed the registry's 401 reply
      // for an agent whose record has not loaded yet.
      resolveClaimLinkResponse({
        response: { error: { code: 401, message: 'Unauthorized' } },
        agentId: input.agentId,
        ...(input.studioId !== undefined ? { studioId: input.studioId } : {}),
        track: emit
      });
      return {};
    }

    if (input.event === 'auth-locked') {
      // The 401 that also locks the camera.
      resolveClaimLinkResponse({
        response: {
          error: { code: 401, message: 'Unauthorized' },
          deviceLoginLockoutStatus: { locked: true, unlockTimeSeconds: 30 }
        },
        agentId: input.agentId,
        ...(input.studioId !== undefined ? { studioId: input.studioId } : {}),
        embedded: true,
        track: emit
      });
      return {};
    }

    if (input.event === 'auth-not-401') {
      // Replies the screen shows as errors that are not a 401: a 400, and a
      // lockout reply for a camera that is still locked from earlier attempts.
      for (const response of [
        { error: { code: 400, message: 'Bad Request' } },
        { deviceLoginLockoutStatus: { locked: true, unlockTimeSeconds: 30 } }
      ]) {
        resolveClaimLinkResponse({
          response,
          agentId: input.agentId,
          ...(input.studioId !== undefined ? { studioId: input.studioId } : {}),
          embedded: true,
          track: emit
        });
      }
      return {};
    }

    if (input.event === 'claim-linked') {
      // The claim screen's success path after the link settles.
      reportClaimLinked({
        agentId: input.agentId,
        studioId: input.studioId,
        embedded: input.embedded,
        track: emit
      });
      const claimClockArmed = isDeviceShadowTimeoutArmed(input.agentId, input.studioId);
      // No camera follows this synthetic claim, so stop its clock rather than
      // leave a timeout to fire into later specs in the same session.
      disarmDeviceShadowTimeout(input.agentId, input.studioId);
      return { claimClockArmed };
    }

    if (input.event === 'add-source-reply') {
      // The Add Source dialog's own reply classifier, with the bag the dialog
      // builds for an Iris camera.
      const outcome = reportAddSourceCredentialReply({
        reply: input.reply,
        authAnalyticsProps: { studio_id: input.studioId, is_embedded: true },
        track: emit
      });
      return { outcome };
    }

    if (input.event === 'shadow-after-claim') {
      return shadowAfterClaim(input);
    }

    if (input.event === 'shadow-add-fails') {
      return shadowAddFails(input);
    }

    if (input.event === 'shadow-add-not-inserted') {
      return shadowAddNotInserted(input);
    }

    if (input.event === 'claim-then-shadow-elsewhere') {
      return claimThenShadowElsewhere(input);
    }

    armDeviceShadowTimeout({
      agentId: input.agentId,
      ...(input.studioId !== undefined ? { studioId: input.studioId } : {}),
      track: emit,
      timeoutMs: 0
    });
    return {};
  };

  // Playwright drives the production claim classifier and success path, the
  // Add Source reply classifier, the shadow timer, and the embedded bootstrap
  // without a camera or the production 60-second wait.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisSimulateDeviceGrainTelemetry = simulate;
}
